const MAGIC = [0x53, 0x4d, 0x52, 0x54] as const
const VERSION = 1
const FIXED_HEADER_BYTES = 4 + 1 + 4 + 4 + 1
const HEADER_PADDING =
  (4 - (FIXED_HEADER_BYTES % 4)) % 4
const HEADER_BYTES = FIXED_HEADER_BYTES + HEADER_PADDING

export type PackedVectorMatrix = {
  count: number
  dimensions: number
  scales: Float32Array
  vectors: Int8Array
}

function assertMatrix(vectors: Float32Array[]) {
  if (vectors.length === 0) {
    throw new Error('Cannot pack an empty vector matrix.')
  }
  const dimensions = vectors[0]?.length ?? 0
  if (dimensions === 0) {
    throw new Error('Vector dimensions must be positive.')
  }
  for (const vector of vectors) {
    if (vector.length !== dimensions) {
      throw new Error('All vectors must have the same dimensions.')
    }
    if (!Array.from(vector).every(Number.isFinite)) {
      throw new Error('Vectors must contain only finite values.')
    }
  }
}

export function packVectorMatrix(
  inputVectors: Float32Array[],
): Uint8Array {
  assertMatrix(inputVectors)
  const count = inputVectors.length
  const dimensions = inputVectors[0]!.length
  const scaleBytes = count * Float32Array.BYTES_PER_ELEMENT
  const vectorBytes = count * dimensions
  const buffer = new ArrayBuffer(
    HEADER_BYTES + scaleBytes + vectorBytes,
  )
  const view = new DataView(buffer)
  let offset = 0

  for (const byte of MAGIC) {
    view.setUint8(offset, byte)
    offset += 1
  }
  view.setUint8(offset, VERSION)
  offset += 1
  view.setUint32(offset, count, true)
  offset += 4
  view.setUint32(offset, dimensions, true)
  offset += 4
  view.setUint8(offset, 1)
  offset += 1
  offset += HEADER_PADDING

  const scales = new Float32Array(buffer, offset, count)
  offset += scaleBytes
  const vectors = new Int8Array(
    buffer,
    offset,
    count * dimensions,
  )

  inputVectors.forEach((vector, row) => {
    let maxAbsolute = 0
    for (const value of vector) {
      maxAbsolute = Math.max(maxAbsolute, Math.abs(value))
    }
    const scale = maxAbsolute > 0 ? maxAbsolute / 127 : 1
    scales[row] = scale
    const base = row * dimensions
    for (let column = 0; column < dimensions; column += 1) {
      const quantized = Math.round((vector[column] ?? 0) / scale)
      vectors[base + column] = Math.max(
        -127,
        Math.min(127, quantized),
      )
    }
  })

  return new Uint8Array(buffer)
}

export function decodeVectorMatrix(
  input: ArrayBuffer | Uint8Array,
): PackedVectorMatrix {
  const byteOffset =
    input instanceof Uint8Array ? input.byteOffset : 0
  const byteLength =
    input instanceof Uint8Array
      ? input.byteLength
      : input.byteLength
  const buffer =
    input instanceof Uint8Array ? input.buffer : input
  const view = new DataView(buffer, byteOffset, byteLength)
  let offset = 0

  for (const expected of MAGIC) {
    if (view.getUint8(offset) !== expected) {
      throw new Error('Invalid SamRouter vector artifact magic.')
    }
    offset += 1
  }
  const version = view.getUint8(offset)
  offset += 1
  if (version !== VERSION) {
    throw new Error(
      `Unsupported SamRouter vector artifact version: ${version}`,
    )
  }
  const count = view.getUint32(offset, true)
  offset += 4
  const dimensions = view.getUint32(offset, true)
  offset += 4
  const dtype = view.getUint8(offset)
  offset += 1
  if (dtype !== 1) {
    throw new Error('Unsupported SamRouter vector dtype.')
  }
  offset += HEADER_PADDING

  const expectedBytes =
    HEADER_BYTES + count * 4 + count * dimensions
  if (byteLength !== expectedBytes) {
    throw new Error(
      `Vector artifact length mismatch: expected ${expectedBytes}, received ${byteLength}.`,
    )
  }

  const scales = new Float32Array(
    buffer,
    byteOffset + offset,
    count,
  )
  offset += count * 4
  const vectors = new Int8Array(
    buffer,
    byteOffset + offset,
    count * dimensions,
  )

  return {
    count,
    dimensions,
    scales,
    vectors,
  }
}

export function dequantizeVector(
  matrix: PackedVectorMatrix,
  row: number,
): Float32Array {
  if (row < 0 || row >= matrix.count) {
    throw new Error(`Vector row ${row} is out of bounds.`)
  }
  const result = new Float32Array(matrix.dimensions)
  const scale = matrix.scales[row] ?? 1
  const base = row * matrix.dimensions
  for (
    let column = 0;
    column < matrix.dimensions;
    column += 1
  ) {
    result[column] =
      (matrix.vectors[base + column] ?? 0) * scale
  }
  return result
}

export function searchVectorMatrix(
  matrix: PackedVectorMatrix,
  query: Float32Array,
  ids: string[],
  limit = 5,
): Array<{ id: string; similarity: number }> {
  if (query.length !== matrix.dimensions) {
    throw new Error(
      `Query has ${query.length} dimensions; expected ${matrix.dimensions}.`,
    )
  }
  if (ids.length !== matrix.count) {
    throw new Error(
      `Vector metadata has ${ids.length} rows; expected ${matrix.count}.`,
    )
  }

  const ranked: Array<{
    id: string
    similarity: number
  }> = []

  for (let row = 0; row < matrix.count; row += 1) {
    const base = row * matrix.dimensions
    const scale = matrix.scales[row] ?? 1
    let dot = 0
    for (
      let column = 0;
      column < matrix.dimensions;
      column += 1
    ) {
      dot +=
        (query[column] ?? 0) *
        (matrix.vectors[base + column] ?? 0) *
        scale
    }

    const entry = {
      id: ids[row]!,
      similarity: dot,
    }
    const position = ranked.findIndex(
      (candidate) => candidate.similarity < dot,
    )
    if (position === -1) {
      ranked.push(entry)
    } else {
      ranked.splice(position, 0, entry)
    }
    if (ranked.length > limit) {
      ranked.length = limit
    }
  }

  return ranked
}

export function normalizeVector(
  vector: Float32Array,
): Float32Array {
  let squaredMagnitude = 0
  for (const value of vector) {
    squaredMagnitude += value * value
  }
  const magnitude = Math.sqrt(squaredMagnitude)
  if (!Number.isFinite(magnitude) || magnitude === 0) {
    throw new Error('Cannot normalize a zero or invalid vector.')
  }
  return Float32Array.from(
    vector,
    (value) => value / magnitude,
  )
}

export function truncateAndNormalize(
  vector: Float32Array,
  dimensions: number,
): Float32Array {
  if (vector.length < dimensions) {
    throw new Error(
      `Cannot truncate ${vector.length} dimensions to ${dimensions}.`,
    )
  }
  return normalizeVector(vector.slice(0, dimensions))
}
